import { useState, useRef, useCallback, useEffect } from 'react'
import { useFormatMessage } from '@/translations/useFormatMessage'
import { CircularProgress } from '@mui/material'
import PhoneInput, { getCountries, isValidPhoneNumber } from 'react-phone-number-input/max'
import type { Country } from 'react-phone-number-input/max'
import 'react-phone-number-input/style.css'
import { CountrySearchSelect } from '../CountrySearchSelect'
import { sanitizePhoneInput } from '@/shared/lib/sanitizePhoneInput'
import { phoneDisplayHasPrefix } from '@/shared/lib/phoneDisplayHasPrefix'
import { requestOtp } from '@/shared/api/otp'
import { normalizeError } from '@/lib/apiErrors'
import { EMAIL_REGEX } from '@/lib/validation'
import { getErrorMessageKey } from '../../lib/errorCodeMapper'
import {
	StyledContentBox,
	StyledPhoneInputContainer,
	StyledErrorBox,
	StyledErrorText,
	StyledButton,
	StyledFooterBox,
	StyledButtonContainer,
	StyledEmailHelperText,
	StyledEmailFieldBox,
	StyledEmailInput,
} from '../../SignInModal.styled'
import { StyledFormRoot, StyledFormInputsWrapper, StyledLoadingContent } from './SignInForm.styled'
import type { StepComponentProps } from '../../SignInModal.types'

interface SignInFormProps extends StepComponentProps {
	onPhoneInputRef?: (ref: HTMLInputElement | null) => void
}

const SUPPORTED_COUNTRIES = new Set<Country>(getCountries())

export function SignInForm({
	state,
	setState,
	locationId,
	onPhoneInputRef,
	locationCountryCode,
}: SignInFormProps) {
	const { t } = useFormatMessage()
	// Lazy initializer (not an effect): SignInForm fully unmounts while the Verify
	// step is shown, so this runs once on remount and seeds the field with the
	// email the user already typed — "Change Email" only needs a correction, not a retype.
	const [emailInput, setEmailInput] = useState(() =>
		state.channel === 'EMAIL' && state.identifier ? state.identifier : '',
	)

	const [selectedCountry, setSelectedCountry] = useState<Country>(() => {
		const normalized = locationCountryCode?.toUpperCase() ?? ''
		return SUPPORTED_COUNTRIES.has(normalized as Country) ? (normalized as Country) : 'US'
	})
	const [emailHasError, setEmailHasError] = useState(false)
	const [phoneInputKey, setPhoneInputKey] = useState(0)
	const phoneInputRef = useRef<HTMLInputElement | null>(null)
	const containerNodeRef = useRef<HTMLDivElement | null>(null)

	const setPhoneInputRef = useCallback(
		(element: HTMLInputElement | null) => {
			phoneInputRef.current = element
			if (onPhoneInputRef) {
				onPhoneInputRef(element)
			}
		},
		[onPhoneInputRef],
	)

	const handlePhoneChange = (value: string | undefined) => {
		const raw = value || ''
		const sanitized = raw ? sanitizePhoneInput(raw, selectedCountry) : ''
		const displayText = phoneInputRef.current?.value ?? ''
		// Remount when: value was normalised by sanitizer (duplicate calling code,
		// trunk zero), OR display shows an international prefix the user typed
		// manually. A fresh mount re-renders the value in national format, which
		// the library won't do for an already-mounted input.
		const displayHasPrefix = phoneDisplayHasPrefix(displayText, sanitized, selectedCountry)
		if (sanitized !== raw || displayHasPrefix) {
			setPhoneInputKey((key) => key + 1)
		}
		setState((prev) => ({
			...prev,
			phoneOrEmailInput: sanitized,
			errorMessage: null,
		}))
	}

	// After a remount the DOM input is a new element: refresh the refs,
	// restore focus and put the caret back at the end.
	useEffect(() => {
		if (phoneInputKey === 0) return
		const input = containerNodeRef.current?.querySelector('input')
		if (!input) return
		setPhoneInputRef(input)
		input.focus()
		input.setSelectionRange(input.value.length, input.value.length)
	}, [phoneInputKey, setPhoneInputRef])

	const handleCountryChange = (country: Country | undefined) => {
		if (country) {
			setSelectedCountry(country)
			// Reset email if switching to US
			if (country === 'US' && emailInput) {
				setEmailInput('')
			}
		}
	}

	const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setEmailInput(e.target.value)
		setEmailHasError(false)
		setState((prev) => ({
			...prev,
			errorMessage: null,
		}))
	}

	// Non-US when either the location country OR the user's picker selection is non-US.
	// Both must be US to use SMS — matching Ember's inline `countryCode === 'US'` guard.
	const isNonUSCountry = selectedCountry !== 'US'
	// RFC 5321 §4.5.3.1.3 sets the maximum email address length at 254 characters.
	// Previously this was incorrectly set to 64 (the per-segment limit, not the total).
	const EMAIL_MAX_LENGTH = 255

	interface EmailValidation {
		valid: boolean
		errorType?: 'length' | 'format'
	}

	const validateEmail = (email: string): EmailValidation => {
		if (!email) return { valid: false, errorType: 'format' }
		if (email.length > EMAIL_MAX_LENGTH) return { valid: false, errorType: 'length' }
		if (!EMAIL_REGEX.test(email)) return { valid: false, errorType: 'format' }
		return { valid: true }
	}

	const handleSendCode = async () => {
		if (!state.phoneOrEmailInput.trim()) {
			const errorMessage = t('signin:errors:emptyPhone')
			setState((prev) => ({
				...prev,
				errorMessage,
			}))
			return
		}

		if (isNonUSCountry && !emailInput.trim()) {
			const errorMessage = t('signin:errors:emptyEmail')
			setEmailHasError(true)
			setState((prev) => ({
				...prev,
				errorMessage,
			}))
			return
		}

		if (isNonUSCountry) {
			const validation = validateEmail(emailInput)
			if (!validation.valid) {
				const errorKey =
					validation.errorType === 'length'
						? 'signin:errors:emailTooLong'
						: 'signin:errors:invalidEmailFormat'
				const errorMessage = t(errorKey)
				setEmailHasError(true)
				setState((prev) => ({
					...prev,
					errorMessage,
				}))
				return
			}
		}

		setState((prev) => ({
			...prev,
			isRequestInProgress: true,
			errorMessage: null,
		}))

		try {
			const channel = selectedCountry === 'US' ? 'SMS' : 'EMAIL'
			const identifier = channel === 'SMS' ? state.phoneOrEmailInput : emailInput

			const response = await requestOtp({
				channel,
				identifier,
				locationId,
			})

			setState((prev) => ({
				...prev,
				isVerifyStepShown: true,
				isRequestInProgress: false,
				correlationId: response.correlationId,
				channel,
				identifier,
			}))
		} catch (err) {
			const apiError = normalizeError(err)
			const errorKey = getErrorMessageKey({
				status: apiError.status ?? 0,
				message: apiError.message,
				code: apiError.code,
			})
			const errorMessage = t(errorKey)

			setState((prev) => ({
				...prev,
				isRequestInProgress: false,
				errorMessage,
			}))
		}
	}

	const handleFormSubmit = (e: React.SyntheticEvent<HTMLFormElement>) => {
		e.preventDefault()
		void handleSendCode()
	}

	const isPhoneValid = isValidPhoneNumber(state.phoneOrEmailInput)
	const isEmailValid = !isNonUSCountry || validateEmail(emailInput).valid
	const isFormValid = isPhoneValid && isEmailValid

	const phoneContainerRef = useCallback(
		(node: HTMLDivElement | null) => {
			containerNodeRef.current = node
			if (node) {
				const inputElement = node.querySelector('input')
				setPhoneInputRef(inputElement)
			} else {
				setPhoneInputRef(null)
			}
		},
		[setPhoneInputRef],
	)

	return (
		<StyledFormRoot onSubmit={handleFormSubmit}>
			<StyledContentBox>
				<StyledFormInputsWrapper>
					<StyledPhoneInputContainer ref={phoneContainerRef}>
						<PhoneInput
							key={phoneInputKey}
							defaultCountry={selectedCountry}
							initialValueFormat="national"
							// Must be undefined (not '') when empty: the library reads an
							// external '' as a value reset and snaps the country back to
							// defaultCountry, wiping the user's selection.
							value={state.phoneOrEmailInput || undefined}
							onChange={handlePhoneChange}
							onCountryChange={handleCountryChange}
							disabled={state.isRequestInProgress}
							placeholder={t('signin:signInForm:phoneLabel')}
							aria-label={t('signin:signInForm:phoneAriaLabel')}
							countrySelectComponent={CountrySearchSelect}
							addInternationalOption={false}
						/>
					</StyledPhoneInputContainer>

					{isNonUSCountry && (
						<>
							<StyledEmailHelperText>
								{t('signin:signInForm:emailHelper')}
							</StyledEmailHelperText>

							<StyledEmailFieldBox hasError={emailHasError}>
								<StyledEmailInput
									id="signin-email"
									type="email"
									value={emailInput}
									onChange={handleEmailChange}
									disabled={state.isRequestInProgress}
									maxLength={EMAIL_MAX_LENGTH}
									placeholder={t('signin:signInForm:emailLabel')}
									aria-label={t('signin:signInForm:emailAriaLabel')}
									aria-required="true"
								/>
							</StyledEmailFieldBox>
						</>
					)}

					{state.errorMessage && (
						<StyledErrorBox role="alert">
							<StyledErrorText>{state.errorMessage}</StyledErrorText>
						</StyledErrorBox>
					)}
				</StyledFormInputsWrapper>
			</StyledContentBox>

			<StyledFooterBox>
				<StyledButtonContainer>
					<StyledButton
						type="submit"
						disabled={!isFormValid || state.isRequestInProgress}
					>
						{state.isRequestInProgress ? (
							<StyledLoadingContent>
								<CircularProgress size={16} sx={{ color: 'inherit' }} />
								{t('signin:signInForm:sendingButton')}
							</StyledLoadingContent>
						) : (
							t('signin:signInForm:sendButton')
						)}
					</StyledButton>
				</StyledButtonContainer>
			</StyledFooterBox>
		</StyledFormRoot>
	)
}
